import { computed, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, fromEvent, map, startWith, filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ThemeService {
    theme$ = new BehaviorSubject('');
    theme = signal('');

    factionTheme$ = new BehaviorSubject('');
    factionTheme = signal('');

    windowSize = toSignal(
        fromEvent(window, 'resize').pipe(
            startWith(window.innerWidth),
            map(() => window.innerWidth)
        )
    );

    isDesktop = computed(() => {
        return this.windowSize() > 991;
    });

    constructor(private router: Router) {
        // Update the theme on every completed navigation by walking the primary
        // route tree. This covers cached-page restoration (back navigation) where
        // component lifecycle hooks such as ngOnInit do not re-fire.
        this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
            let route = this.router.routerState.root;
            let theme = 'home';
            while (route) {
                const data = route.snapshot.data;
                if (data['theme'] || data['gameId']) {
                    theme = data['theme'] || data['gameId'];
                }
                route = route.children.find((c) => c.outlet === 'primary') ?? null;
            }
            this.selectTheme(theme);
        });
    }

    selectTheme(name: string) {
        // Only update if theme actually changed to prevent unnecessary emissions
        if (this.theme$.value !== name) {
            console.log('[ThemeService] Changing theme from', this.theme$.value, 'to', name);
            this.theme$.next(name);
            this.theme.set(name);
            // Clear faction theme on game change; game-specific services will re-apply it
            this.setFactionTheme('');
        } else {
            console.log('[ThemeService] Theme already set to', name, '- skipping');
        }
    }

    setFactionTheme(faction: string) {
        if (this.factionTheme$.value !== faction) {
            this.factionTheme$.next(faction);
            this.factionTheme.set(faction);
        }
    }
}
